import { Injectable } from "@angular/core";
import { ChecklistStateStore } from "./checklist-state.store";
import { ChecklistStateQuery } from "./checklist-state.query";
import { addMinutes, isAfter } from "date-fns";
import { combineLatest, filter, interval } from "rxjs";
import { RaygunService } from "source/app/configuration/services/raygun.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { IsClearedForOperationDetails } from "source/app/configuration/state/location-state.store";

@Injectable({ providedIn: "root" })
export class ChecklistStateService {
	constructor(
		private checklistStateStore: ChecklistStateStore,
		private checklistStateQuery: ChecklistStateQuery,
		private locationStateQuery: LocationStateQuery,
		private raygunService: RaygunService,
	) {
		this.activate();
	}

	setPreopeningChecklistSubmitted = (resultId: string) => {
		this.checklistStateStore.store.update(() => ({
			preopeningChecklistSubmitted: { result: resultId, submitted: new Date().toISOString() },
		}));
	};

	clearPreopeningChecklistSubmitted = () => {
		this.checklistStateStore.store.update(() => ({ preopeningChecklistSubmitted: null }));
	};

	private shouldClearPreopeningChecklistSubmittedOn = (details: IsClearedForOperationDetails) => {
		const noPreopeningCheclistConfigured = details.preopeningChecklists.length === 0;
		if (noPreopeningCheclistConfigured) {
			return true;
		}

		// As we currently only support 1 preopening checklist, we just work with the first for now.
		const isFirstPreopeningChecklistCompleted = details.preopeningChecklists[0].completed != null;
		return isFirstPreopeningChecklistCompleted;
	};

	private activate = () => {
		const maxAgeMinutes = 5;

		this.locationStateQuery.isClearedForOperationDetails$.subscribe((isClearedForOperationDetails) => {
			if (this.shouldClearPreopeningChecklistSubmittedOn(isClearedForOperationDetails)) {
				// Clear the local submitted date, as we from this point on, should only rely on the server information
				this.clearPreopeningChecklistSubmitted();
			}
		});

		combineLatest([
			this.checklistStateQuery.preopeningChecklistSubmitted$,
			this.locationStateQuery.lastServerUpdate$,
			interval(5000),
		])
			.pipe(filter(([preopeningChecklist]) => preopeningChecklist !== null))
			.subscribe(([preopeningChecklist, lastServerUpdate]) => {
				const validUntil = addMinutes(preopeningChecklist.submitted, maxAgeMinutes);

				if (isAfter(lastServerUpdate, validUntil)) {
					//The Result should be included in the location status by now
					//we remove it from submitted, and log it to RayGun
					this.raygunService.send(new Error(`Result is not completed after ${maxAgeMinutes} minutes`), {
						resultId: preopeningChecklist.result,
						submitted: preopeningChecklist.submitted,
						lastServerUpdate,
					});
					this.clearPreopeningChecklistSubmitted();
				}
			});
	};
}
